import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { SimulatedUser, AppNotification, notificationsApi } from '../../services/api';
import axios from 'axios';
import {
  Search, Bell, ChevronDown, ChevronUp, ArrowLeftRight, LogOut, Lock, Undo2,
  ShieldAlert, Eye, Calendar, Building2, Globe,
  FileEdit, Megaphone, CheckCircle2, MessageCircle, Handshake, PartyPopper,
  RefreshCw, Rocket, ClipboardList, Truck, Link2, Mail, CheckCheck
} from 'lucide-react';
import TimezoneClock from '../TimezoneClock/TimezoneClock';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import RecentItems from '../RecentItems/RecentItems';
import { bypassNavigationBlock } from '../../hooks/useNavigationBlock';
import AboutModal from '../AboutModal/AboutModal';

/** Map notification type to a compact icon */
const NOTIF_TYPE_ICONS: Record<string, React.ReactNode> = {
  REQUEST_CREATED: <FileEdit size={14} />,
  REQUEST_PUBLISHED: <Megaphone size={14} />,
  REQUEST_CLOSED: <Lock size={14} />,
  REQUEST_COMPLETED: <CheckCircle2 size={14} />,
  COMMENT_ADDED: <MessageCircle size={14} />,
  OFFER_RECEIVED: <Handshake size={14} />,
  OFFER_ACCEPTED: <PartyPopper size={14} />,
  STATUS_CHANGED: <RefreshCw size={14} />,
  SELECTION_STARTED: <Search size={14} />,
  MISSION_CREATED: <Rocket size={14} />,
  MISSION_STATUS_UPDATED: <ClipboardList size={14} />,
  TRANSPORT_PARTNER_NAMED: <Truck size={14} />,
  TRANSPORT_EXTENSION_RECEIVED: <Link2 size={14} />
};

/** i18n keys for notification types */
const NOTIF_TYPE_LABEL_KEYS: Record<string, string> = {
  REQUEST_CREATED: 'notifications.requestCreated',
  REQUEST_PUBLISHED: 'notifications.requestPublished',
  REQUEST_CLOSED: 'notifications.requestClosed',
  REQUEST_COMPLETED: 'notifications.requestCompleted',
  COMMENT_ADDED: 'notifications.commentAdded',
  OFFER_RECEIVED: 'notifications.offerReceived',
  OFFER_ACCEPTED: 'notifications.offerAccepted',
  STATUS_CHANGED: 'notifications.statusChanged',
  SELECTION_STARTED: 'notifications.selectionStarted',
  MISSION_CREATED: 'notifications.missionCreated',
  MISSION_STATUS_UPDATED: 'notifications.missionStatusUpdated',
  TRANSPORT_PARTNER_NAMED: 'notifications.transportPartnerNamed',
  TRANSPORT_EXTENSION_RECEIVED: 'notifications.transportExtensionReceived'
};

/** Format an ISO timestamp into a relative time string using i18n */
function useFormatRelativeTime() {
  const { t } = useTranslation();
  return (isoDate: string): string => {
    const diff = Date.now() - new Date(isoDate).getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return t('common.justNow');
    if (minutes < 60) return t('common.minutesAgo', { count: minutes });
    if (hours < 24) return t('common.hoursAgo', { count: hours });
    if (days === 1) return t('common.yesterday');
    if (days < 30) return t('common.daysAgo', { count: days });
    return t('common.monthsAgo', { count: Math.floor(days / 30) });
  };
}

function Header() {
  const { t } = useTranslation();
  const formatRelativeTime = useFormatRelativeTime();
  const { user, token, logout, isImpersonating, originalUser, impersonate, stopImpersonating, effectiveRoles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [exerciseOpen, setExerciseOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [myPatientsOpen, setMyPatientsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [nationSwitcherOpen, setNationSwitcherOpen] = useState(false);
  const [simulatedUsers, setSimulatedUsers] = useState<SimulatedUser[]>([]);
  const [switcherLoading, setSwitcherLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifPopupOpen, setNotifPopupOpen] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState<AppNotification[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifPopupRef = useRef<HTMLDivElement>(null);

  // Poll unread notification count every 15 seconds
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationsApi.getUnreadCount();
      setUnreadCount(res.data.count || 0);
    } catch {
      // Silently ignore errors
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    window.addEventListener('notifications-changed', fetchUnreadCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications-changed', fetchUnreadCount);
    };
  }, [fetchUnreadCount]);

  // Fetch recent notifications when popup opens
  useEffect(() => {
    if (notifPopupOpen) {
      setNotifLoading(true);
      notificationsApi.getAll({ limit: 5, offset: 0 })
        .then(res => {
          setRecentNotifications(res.data.notifications || []);
        })
        .catch(() => { /* silently ignore */ })
        .finally(() => setNotifLoading(false));
    }
  }, [notifPopupOpen]);

  // Close notification popup on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifPopupRef.current && !notifPopupRef.current.contains(e.target as Node)) {
        setNotifPopupOpen(false);
      }
    }
    if (notifPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notifPopupOpen]);

  // Close notification popup on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setNotifPopupOpen(false);
      }
    }
    if (notifPopupOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [notifPopupOpen]);

  // Handle mark all as read from popup
  const handlePopupMarkAllRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setRecentNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      window.dispatchEvent(new Event('notifications-changed'));
    } catch {
      // silently ignore
    }
  };

  // Handle clicking a notification in the popup
  const handlePopupNotifClick = async (notification: AppNotification) => {
    if (!notification.isRead) {
      try {
        await notificationsApi.markAsRead(notification.id);
        setRecentNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
        window.dispatchEvent(new Event('notifications-changed'));
      } catch {
        // silently ignore
      }
    }
    setNotifPopupOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const canImpersonate = originalUser
    ? (originalUser.isExcon || originalUser.isAdmin)
    : (user?.isExcon || user?.isAdmin);

  // Load simulated users when switcher sub-menu opens
  const API_URL = process.env.REACT_APP_API_URL || '';
  const originalToken = localStorage.getItem('originalToken');
  useEffect(() => {
    if (nationSwitcherOpen && canImpersonate && simulatedUsers.length === 0) {
      setSwitcherLoading(true);
      const authToken = originalToken || token;
      axios.get(`${API_URL}/api/auth/simulated-users`, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
        .then(res => setSimulatedUsers(res.data))
        .catch(() => {})
        .finally(() => setSwitcherLoading(false));
    }
  }, [nationSwitcherOpen, canImpersonate, simulatedUsers.length, originalToken, token, API_URL]);

  const handleImpersonate = async (userId: string) => {
    try {
      await impersonate(userId);
      // Bypass any navigation block before full page reload
      bypassNavigationBlock();
      window.location.href = '/';
    } catch (err) {
      console.error('Impersonation failed:', err);
    }
  };

  const handleStopImpersonating = () => {
    stopImpersonating();
    // Bypass any navigation block before full page reload
    bypassNavigationBlock();
    window.location.href = '/';
  };

  // Map raw role string to descriptive label
  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'ADMIN': return t('roles.admin');
      case 'EXCON': return t('roles.excon');
      case 'EU': return t('roles.eu');
      case 'PLANNER': return t('roles.planner');
      case 'HUB': return t('roles.hub');
      case 'NATION': return t('roles.nation');
      default: return role;
    }
  };

  // Determine the user's primary role display (highest priority first)
  const getUserRoleDisplay = () => {
    if (user?.isAdmin) return { icon: <ShieldAlert size={16} />, label: t('roles.admin'), badge: 'A', badgeClass: 'badge-admin' };
    if (user?.isExcon) return { icon: <Eye size={16} />, label: t('roles.excon'), badge: 'X', badgeClass: 'badge-excon' };
    if (user?.isEU) return { icon: <Globe size={16} />, label: t('roles.eu'), badge: 'EU', badgeClass: 'badge-eu' };
    if (user?.isPlanner) return { icon: <Calendar size={16} />, label: t('roles.planner'), badge: 'P', badgeClass: 'badge-planner' };
    if (user?.isHub) return { icon: <Building2 size={16} />, label: t('roles.hub'), badge: 'H', badgeClass: 'badge-hub' };
    return { icon: null, label: user?.nation || t('roles.nation'), badge: 'N', badgeClass: 'badge-nation' };
  };

  const roleDisplay = getUserRoleDisplay();

  // Helper function to check if a link is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
    <header className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="logo-container">
              <img src="/logo_arcos.webp" alt="ARCOS Logo" className="header-logo-icon" />
              <div className="logo-text">
                <span className="logo-title">ARCOS</span>
                <span className="logo-subtitle">Advanced Request Communication System</span>
              </div>
            </div>

            {/* Hamburger Menu Button */}
            <button
              className="hamburger-menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            <nav className={mobileMenuOpen ? 'mobile-open' : ''}>
              <ul className="nav-links">
                <li>
                  <Link to="/" className={isActive('/') ? 'active' : ''}>{t('nav.dashboard')}</Link>
                </li>

                <li>
                  <Link to="/requests" className={isActive('/requests') ? 'active' : ''}>{t('nav.requests')}</Link>
                </li>

                {/* My Patients dropdown: Treatment + Transport — all roles except HUB-only */}
                {(
                  <li
                    className="dropdown-parent"
                    onMouseEnter={() => setMyPatientsOpen(true)}
                    onMouseLeave={() => setMyPatientsOpen(false)}
                  >
                    <span className={`dropdown-trigger${isActive('/my-patients') || isActive('/my-transport-patients') ? ' active' : ''}`}>{t('nav.myPatients')} <ChevronDown size={14} /></span>
                    {myPatientsOpen && (
                      <ul className="dropdown-menu">
                        <li>
                          <Link to="/my-patients" className={isActive('/my-patients') ? 'active' : ''}>{t('nav.myPatientsTreatment', 'Treatment')}</Link>
                        </li>
                        <li>
                          <Link to="/my-transport-patients" className={isActive('/my-transport-patients') ? 'active' : ''}>{t('nav.myPatientsTransport', 'Transport')}</Link>
                        </li>
                      </ul>
                    )}
                  </li>
                )}

                {/* Missions: hide for HUB-only users */}
                {!(effectiveRoles.isHub && !effectiveRoles.isNation && !effectiveRoles.isAdmin && !effectiveRoles.isExcon && !effectiveRoles.isEU && !effectiveRoles.isPlanner) && (
                  <li>
                    <Link to="/missions" className={isActive('/missions') ? 'active' : ''}>{t('nav.missions', 'Missions')}</Link>
                  </li>
                )}

                {(effectiveRoles.isPlanner || effectiveRoles.isNation || effectiveRoles.isAdmin) && (
                  <li>
                    <Link to="/transport-planning" className={isActive('/transport-planning') ? 'active' : ''}>{t('nav.transportPlanning', 'Planning')}</Link>
                  </li>
                )}

                {/* Resources dropdown — visible to all roles */}
                <li
                  className="dropdown-parent"
                  onMouseEnter={() => setResourcesOpen(true)}
                  onMouseLeave={() => setResourcesOpen(false)}
                >
                  <span className="dropdown-trigger">{t('nav.resources')} <ChevronDown size={14} /></span>
                  {resourcesOpen && (
                    <ul className="dropdown-menu">
                      <li>
                        <Link to="/assets" className={isActive('/assets') ? 'active' : ''}>{t('nav.assets')}</Link>
                      </li>
                      <li>
                        <Link to="/hospital" className={isActive('/hospital') ? 'active' : ''}>{t('nav.treatmentFacilities')}</Link>
                      </li>
                      <li>
                        <Link to="/startpoints" className={isActive('/startpoints') ? 'active' : ''}>{t('nav.hubs')}</Link>
                      </li>
                      {(effectiveRoles.isAdmin || effectiveRoles.isExcon) && (
                        <>
                          <li className="dropdown-divider"></li>
                          <li>
                            <Link to="/asset-contracts" className={isActive('/asset-contracts') ? 'active' : ''}>{t('nav.assetContracts')}</Link>
                          </li>
                        </>
                      )}
                    </ul>
                  )}
                </li>

                {(effectiveRoles.isExcon || effectiveRoles.isAdmin) && (
                  <li
                    className="dropdown-parent"
                    onMouseEnter={() => setExerciseOpen(true)}
                    onMouseLeave={() => setExerciseOpen(false)}
                  >
                    <span className="dropdown-trigger">{t('nav.exercise')} <ChevronDown size={14} /></span>
                    {exerciseOpen && (
                      <ul className="dropdown-menu">
                        {effectiveRoles.isAdmin && (
                          <li>
                            <Link to="/patient-import" className={isActive('/patient-import') ? 'active' : ''}>{t('nav.patientImport')}</Link>
                          </li>
                        )}
                        <li>
                          <Link to="/asset-templates" className={isActive('/asset-templates') ? 'active' : ''}>{t('nav.assetTemplates')}</Link>
                        </li>
                        <li>
                          <Link to="/day-templates" className={isActive('/day-templates') ? 'active' : ''}>{t('nav.dayTemplates')}</Link>
                        </li>
                        <li className="dropdown-divider"></li>
                        <li>
                          <Link to="/announcement-ticker" className={isActive('/announcement-ticker') ? 'active' : ''}>{t('nav.announcementTicker')}</Link>
                        </li>
                        <li>
                          <Link to="/exercise-analytics" className={isActive('/exercise-analytics') ? 'active' : ''}>{t('nav.analytics', 'Analytics')}</Link>
                        </li>
                        <li>
                          <Link to="/excon-statistics" className={isActive('/excon-statistics') ? 'active' : ''}>{t('nav.exconStatistics', 'EXCON Statistik')}</Link>
                        </li>
                        <li>
                          <Link to="/data-consistency" className={isActive('/data-consistency') ? 'active' : ''}>{t('nav.dataConsistency', 'Data Consistency')}</Link>
                        </li>
                        <li className="dropdown-divider"></li>
                        <li>
                          <Link to="/exercise-control" className={isActive('/exercise-control') ? 'active' : ''}>{t('nav.exerciseControl')}</Link>
                        </li>
                        <li>
                          <Link to="/inject-control" className={isActive('/inject-control') ? 'active' : ''}>{t('nav.injectControl', 'Inject Control')}</Link>
                        </li>
                      </ul>
                    )}
                  </li>
                )}

                {effectiveRoles.isAdmin && (
                  <li
                    className="dropdown-parent"
                    onMouseEnter={() => setAdminOpen(true)}
                    onMouseLeave={() => setAdminOpen(false)}
                  >
                    <span className="dropdown-trigger">{t('nav.administration')} <ChevronDown size={14} /></span>
                    {adminOpen && (
                      <ul className="dropdown-menu">
                        <li>
                          <Link to="/users" className={isActive('/users') ? 'active' : ''}>{t('nav.users')}</Link>
                        </li>
                        <li>
                          <Link to="/settings" className={isActive('/settings') ? 'active' : ''}>{t('nav.settings')}</Link>
                        </li>
                        <li>
                          <Link to="/audit-logs" className={isActive('/audit-logs') ? 'active' : ''}>{t('nav.auditLog')}</Link>
                        </li>
                        <li>
                          <Link to="/data-sync" className={isActive('/data-sync') ? 'active' : ''}>{t('nav.dataSync')}</Link>
                        </li>
                        <li>
                          <Link to="/system-health" className={isActive('/system-health') ? 'active' : ''}>{t('nav.systemHealth', 'System Health')}</Link>
                        </li>
                        <li className="dropdown-divider"></li>
                        <li>
                          <Link to="/exercise-analytics" className={isActive('/exercise-analytics') ? 'active' : ''}>{t('nav.analytics', 'Analytics')}</Link>
                        </li>
                      </ul>
                    )}
                  </li>
                )}

                <li>
                  <Link to="/map" className={isActive('/map') ? 'active' : ''}>{t('nav.map')}</Link>
                </li>

                <li
                  className="dropdown-parent"
                  onMouseEnter={() => setInfoOpen(true)}
                  onMouseLeave={() => setInfoOpen(false)}
                >
                  <span className="dropdown-trigger">{t('nav.info')} <ChevronDown size={14} /></span>
                  {infoOpen && (
                    <ul className="dropdown-menu">
                      <li>
                        <Link to="/documentation" className={isActive('/documentation') ? 'active' : ''}>{t('nav.documentation')}</Link>
                      </li>
                      <li>
                        <Link to="/support" className={isActive('/support') ? 'active' : ''}>{t('nav.support')}</Link>
                      </li>
                      <li>
                        <button className="dropdown-link-btn" onClick={() => { setInfoOpen(false); setShowAboutModal(true); }}>{t('nav.about')}</button>
                      </li>
                    </ul>
                  )}
                </li>
              </ul>
            </nav>
          </div>

          <div className="header-right">
            {/* Timezone Clock */}
            <TimezoneClock />

            {/* Search (Ctrl+K) */}
            <button
              className="header-search-btn"
              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
              title="Search (Ctrl+K)"
            >
              <Search size={16} />
              <kbd className="header-search-kbd">Ctrl+K</kbd>
            </button>

            {/* Recent Items */}
            <RecentItems />

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Bell icon with unread count + notification popup */}
            <div className="notif-popup-container" ref={notifPopupRef}>
              <button
                className="header-bell"
                onClick={() => setNotifPopupOpen(prev => !prev)}
                title="Notifications"
                aria-expanded={notifPopupOpen}
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="header-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </button>

              {notifPopupOpen && (
                <div className="notif-popup" role="menu">
                  <div className="notif-popup-header">
                    <span className="notif-popup-title">{t('nav.notifications')}</span>
                    {unreadCount > 0 && (
                      <button className="notif-popup-mark-all" onClick={handlePopupMarkAllRead}>
                        <CheckCheck size={13} />
                        {t('notifications.markAllRead')}
                      </button>
                    )}
                  </div>

                  <div className="notif-popup-body">
                    {notifLoading ? (
                      <div className="notif-popup-empty">{t('common.loading')}</div>
                    ) : recentNotifications.length === 0 ? (
                      <div className="notif-popup-empty">{t('notifications.noNotifications')}</div>
                    ) : (
                      recentNotifications.map(n => (
                        <button
                          key={n.id}
                          className={`notif-popup-item${!n.isRead ? ' notif-unread' : ''}`}
                          onClick={() => handlePopupNotifClick(n)}
                          role="menuitem"
                        >
                          <span className="notif-popup-item-icon">
                            {NOTIF_TYPE_ICONS[n.type] || <Mail size={14} />}
                          </span>
                          <div className="notif-popup-item-content">
                            <span className="notif-popup-item-label">
                              {NOTIF_TYPE_LABEL_KEYS[n.type] ? t(NOTIF_TYPE_LABEL_KEYS[n.type]) : n.type.replace(/_/g, ' ')}
                            </span>
                            <span className="notif-popup-item-message">{n.title}</span>
                          </div>
                          <div className="notif-popup-item-meta">
                            <span className="notif-popup-item-time">{formatRelativeTime(n.createdAt)}</span>
                            {!n.isRead && <span className="notif-popup-item-dot" />}
                          </div>
                        </button>
                      ))
                    )}
                  </div>

                  <div className="notif-popup-footer">
                    <button
                      className="notif-popup-view-all"
                      onClick={() => { setNotifPopupOpen(false); navigate('/notifications'); }}
                    >
                      {t('notifications.viewAll')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="user-menu">
              <div
                className="user-info"
                onClick={() => setShowDropdown(!showDropdown)}
                title={`${roleDisplay.label}${user?.nation ? ` — ${user.nation}` : ''}`}
              >
                <div className="user-avatar">
                  {user?.flagImage ? (
                    <img src={user.flagImage} alt={user.nation} />
                  ) : (
                    <div className="avatar-role-icon">
                      {roleDisplay.icon || <span>{user?.nation?.substring(0, 2).toUpperCase()}</span>}
                    </div>
                  )}
                  <span className={`avatar-role-badge ${roleDisplay.badgeClass}`}>
                    {roleDisplay.badge}
                  </span>
                </div>
                <div className="user-details">
                  <div className="user-name">
                    <span className="header-role-icon">
                      {roleDisplay.label}
                    </span>
                  </div>
                  <div className="user-id">{user?.displayName || user?.username || user?.email}</div>
                </div>
              </div>

              {showDropdown && (
                <div
                  className="user-dropdown"
                  onMouseLeave={() => { setShowDropdown(false); setNationSwitcherOpen(false); }}
                >
                  {/* Return to original user when impersonating */}
                  {canImpersonate && isImpersonating && (
                    <button className="dropdown-link return-link" onClick={handleStopImpersonating}>
                      <Undo2 size={14} /> {t('auth.returnTo', { nation: originalUser?.isAdmin ? getRoleLabel('ADMIN') : originalUser?.isExcon ? getRoleLabel('EXCON') : originalUser?.nation })}
                    </button>
                  )}

                  {/* Switch Nation sub-menu */}
                  {canImpersonate && (
                    <div className="dropdown-sim-section">
                      <button
                        className="dropdown-link sim-toggle"
                        onClick={() => setNationSwitcherOpen(!nationSwitcherOpen)}
                      >
                        <ArrowLeftRight size={14} /> {effectiveRoles.isAdmin ? t('auth.switchUser', 'Switch User') : t('auth.switchNation')} {nationSwitcherOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {nationSwitcherOpen && (
                        <div className="sim-user-list">
                          {switcherLoading ? (
                            <div className="sim-user-loading">{t('common.loading')}</div>
                          ) : simulatedUsers
                            .filter(su => {
                              if (su.id === user?.id) return false;
                              if (originalUser?.isExcon && !originalUser?.isAdmin && su.role === 'EXCON') return false;
                              return true;
                            })
                            .sort((a, b) => (a.displayName || a.nation || a.role).localeCompare(b.displayName || b.nation || b.role))
                            .map(su => (
                              <button
                                key={su.id}
                                className="sim-user-item"
                                onClick={() => handleImpersonate(su.id)}
                              >
                                {su.flagImage ? (
                                  <img src={su.flagImage} alt={su.nation} className="sim-user-flag" />
                                ) : (
                                  <span className="sim-user-role-icon">
                                    {su.role === 'ADMIN' && <ShieldAlert size={14} />}
                                    {su.role === 'EXCON' && <Eye size={14} />}
                                    {su.role === 'EU' && <Globe size={14} />}
                                    {su.role === 'PLANNER' && <Calendar size={14} />}
                                    {su.role === 'HUB' && <Building2 size={14} />}
                                  </span>
                                )}
                                <span className="sim-user-nation">{su.displayName || su.nation || su.role}</span>
                                <span className="sim-user-role">{getRoleLabel(su.role)}</span>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  )}

                  <Link to="/change-password" className="dropdown-link" onClick={() => setShowDropdown(false)}>
                    <Lock size={14} /> {t('auth.changePassword')}
                  </Link>
                  <button onClick={handleLogout} className="logout">
                    <LogOut size={14} /> {t('auth.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
    </header>

    <AboutModal isOpen={showAboutModal} onClose={() => setShowAboutModal(false)} />
    </>
  );
}

export default Header;
